import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FooterComponent } from '../footer/footer.component';
import { ThemeService } from '../../services/theme.service';
import { TranslationSyncService } from '../../services/translation-sync.service';
import { ScrollStateService } from '../../services/scroll-state.service';

@Component({
  selector: 'app-about-us',
  imports: [CommonModule, FooterComponent],
  templateUrl: './about-us.component.html',
  styleUrl: './about-us.component.css'
})
export class AboutUsComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('containerAbout', { static: false }) containerRef!: ElementRef<HTMLElement>;
  @ViewChild('wrapper', { read: ElementRef }) wrapper!: ElementRef<HTMLElement>;

  // ✅ zeigt auf .details-panel .scroll-bleed im Light DOM
  @ViewChild('aboutDetailsScroll', { static: false }) detailsScrollRef!: ElementRef<HTMLElement>;

  @Input() activeFace: number = 0;
  @Output() toggleImprint = new EventEmitter<void>();
  @Output() togglePrivacy = new EventEmitter<void>();

  showMore = false;
  delayedImageSrc: string = 'about-us/hero_sitting_dark.webp';
  isDarkMode: boolean;

  private darkContainer!: HTMLElement;

  // --- Scroll Auto-Hide (wie Blog) ---
  private readonly MIN_DELTA = 8;
  private last = 0;
  private curr = 0;
  private ticking = false;

  // listener refs (damit wir sauber entfernen können)
  private _onLightDetailsScroll?: (ev: Event) => void;
  private _onDarkDetailsScroll?: (ev: Event) => void;

  // Wrapper logging
  private wrapperRO?: ResizeObserver;
  private wrapperResizeCb?: () => void;

  constructor(
    private renderer: Renderer2,
    private themeService: ThemeService,
    private sync: TranslationSyncService,
    public scrollState: ScrollStateService
  ) { }

  ngOnInit(): void {
    this.themeService.isDarkMode$.subscribe(mode => {
      this.isDarkMode = mode;
      Promise.resolve().then(() => {
        const triggeredByToggle = this.themeService.wasThemeToggled();
        this.updateTheme(triggeredByToggle);
      });
    });
  }

  ngAfterViewInit(): void {
    const container = this.containerRef?.nativeElement;
    this.darkContainer = document.getElementById('dark-container-about') as HTMLElement;

    if (!container || !this.darkContainer) return;

    // i18n register
    this.registerAll(container);
    this.registerAll(this.darkContainer);

    // Dark-Container initial ausblenden
    this.renderer.removeClass(this.darkContainer, 'active');

    // Theme initial anwenden
    this.updateTheme(false);

    // Dark "View more" (falls im Dark DOM als echte Node vorhanden)
    const viewMoreButton = this.darkContainer.querySelector('.view-more-btn') as HTMLElement | null;
    if (viewMoreButton) {
      this.renderer.listen(viewMoreButton, 'click', (e: Event) => {
        e.preventDefault?.();
        this.toggleDetails();
      });
    }

    const closeButton = this.darkContainer.querySelector('.close-btn') as HTMLElement | null;
    if (closeButton) {
      this.renderer.listen(closeButton, 'click', (e: Event) => {
          e.preventDefault?.();
          this.closeDetail();
      });
    }

    // ✅ Scroll-Listener setzen (Light + Dark)
    this.bindDetailScrollListeners();

    // ✅ initiale Sichtbarkeit/State spiegeln
    this.applyVisibilityState(container);
    this.applyVisibilityState(this.darkContainer);

    // Wrapper logging (dein bestehender Wunsch)
    this.initWrapperLogging();
  }

  ngOnDestroy(): void {
    // remove scroll listeners
    const light = this.detailsScrollRef?.nativeElement;
    if (light && this._onLightDetailsScroll) {
      light.removeEventListener('scroll', this._onLightDetailsScroll);
    }

    const dark = this.getDarkDetailsScroller();
    if (dark && this._onDarkDetailsScroll) {
      dark.removeEventListener('scroll', this._onDarkDetailsScroll);
    }

    // wrapper logging cleanup
    this.wrapperRO?.disconnect();
    if (this.wrapperResizeCb) {
      window.removeEventListener('resize', this.wrapperResizeCb);
    }
  }

  // -------------------------
  // i18n helper
  // -------------------------
  private registerAll(root: HTMLElement) {
    root.querySelectorAll<HTMLElement>('[data-translate]').forEach(el => {
      const key = el.getAttribute('data-translate')!;
      this.sync.register(el, key);
    });
  }

  // -------------------------
  // Theme / visibility
  // -------------------------
  private updateTheme(triggeredByToggle: boolean): void {
    if (!this.darkContainer || !this.containerRef?.nativeElement) return;

    // intro/detail sync + scroll sync
    this.updateVisibility();
    this.updateScrollHeight();

    // Image sync
    this.themeService.updateImageBasedOnTheme(this.isDarkMode, triggeredByToggle, this.darkContainer);

    // Theme toggle
    this.themeService.toggleThemeMode(
      this.isDarkMode,
      this.renderer,
      this.containerRef.nativeElement,
      this.darkContainer
    );

    // after theme change, ensure classes applied
    this.applyVisibilityState(this.containerRef.nativeElement);
    this.applyVisibilityState(this.darkContainer);
  }

  toggleDetails(): void {
    this.showMore = !this.showMore;
    this.updateVisibility();
    this.updateScrollHeight(); // beim Umschalten scroll pos syncen

    if (!this.showMore) {
      // zurück zum Intro: Bars wieder zeigen (wie Blog list-mode)
      this.forceShowChrome();
    } else {
      // Details gestartet -> initiale last/curr setzen
      this.resetScrollBaseline();
    }

    this.applyVisibilityState(this.containerRef?.nativeElement);
    this.applyVisibilityState(this.darkContainer);
  }

  closeDetail(): void {
    if (!this.showMore) return;
    this.showMore = false;
    this.updateVisibility();
    this.forceShowChrome();
    this.applyVisibilityState(this.containerRef?.nativeElement);
    this.applyVisibilityState(this.darkContainer);
  }

  private updateVisibility(): void {
    const lightIntro = this.containerRef.nativeElement.querySelector('.intro-panel') as HTMLElement | null;
    const lightDetails = this.containerRef.nativeElement.querySelector('.details-panel') as HTMLElement | null;

    const darkIntro = this.darkContainer.querySelector('.intro-panel') as HTMLElement | null;
    const darkDetails = this.darkContainer.querySelector('.details-panel') as HTMLElement | null;

    const introPanel = this.isDarkMode ? darkIntro : lightIntro;
    const detailsPanel = this.isDarkMode ? darkDetails : lightDetails;

    if (!introPanel || !detailsPanel) return;

    if (this.showMore) {
      this.renderer.addClass(introPanel, 'slide-up');
      this.renderer.addClass(detailsPanel, 'show');
    } else {
      this.renderer.removeClass(introPanel, 'slide-up');
      this.renderer.removeClass(detailsPanel, 'show');
    }

    // optional: auch das jeweils andere Theme synchron halten (damit toggle nicht “springt”)
    if (lightIntro && darkIntro && lightDetails && darkDetails) {
      if (this.showMore) {
        lightIntro.classList.add('slide-up');
        darkIntro.classList.add('slide-up');
        lightDetails.classList.add('show');
        darkDetails.classList.add('show');
      } else {
        lightIntro.classList.remove('slide-up');
        darkIntro.classList.remove('slide-up');
        lightDetails.classList.remove('show');
        darkDetails.classList.remove('show');
      }
    }
  }

  // -------------------------
  // Scroll sync light/dark (Details)
  // -------------------------
  private updateScrollHeight(): void {
    const lightScroller = this.getLightDetailsScroller();
    const darkScroller = this.getDarkDetailsScroller();
    if (!lightScroller || !darkScroller) return;

    if (this.isDarkMode) {
      darkScroller.scrollTop = lightScroller.scrollTop || 0;
    } else {
      lightScroller.scrollTop = darkScroller.scrollTop || 0;
    }
  }

  private getLightDetailsScroller(): HTMLElement | null {
    return this.detailsScrollRef?.nativeElement || null; // #aboutDetailsScroll auf .scroll-bleed
  }

  private getDarkDetailsScroller(): HTMLElement | null {
    // Dark: gleiche Struktur wie Light: .details-panel .scroll-bleed
    return this.darkContainer?.querySelector('.details-panel .scroll-bleed') as HTMLElement | null;
  }

  // -------------------------
  // Auto-hide chrome (Navbar/Dock/Footer) – wie Blog
  // -------------------------
  private bindDetailScrollListeners(): void {
    const light = this.getLightDetailsScroller();
    const dark = this.getDarkDetailsScroller();

    // Light
    if (light) {
      this.last = light.scrollTop || 0;

      this._onLightDetailsScroll = (ev: Event) => this.handler(ev);
      light.addEventListener('scroll', this._onLightDetailsScroll, { passive: true });
    }

    // Dark
    if (dark) {
      this._onDarkDetailsScroll = (ev: Event) => this.handler(ev);
      dark.addEventListener('scroll', this._onDarkDetailsScroll, { passive: true });
    }
  }

  private handler = (ev: Event) => {
    if (!this.showMore) return; // ✅ nur Details: Auto-Hide

    const el = ev.target as HTMLElement | null;
    this.curr = el ? el.scrollTop : 0;

    if (!this.ticking) {
      this.ticking = true;
      requestAnimationFrame(() => this.updateChromeFromScroll());
    }
  };

  private updateChromeFromScroll(): void {
    const curr = this.curr;
    const diff = curr - this.last;
    const abs = Math.abs(diff);

    const showAtTop = this.getShowAtTopPx();

    if (curr <= showAtTop) {
      this.scrollState.setNavbarHidden(false);
      this.scrollState.setFooterHidden(false);
      this.scrollState.setDockHidden(false);
    } else if (abs > this.MIN_DELTA) {
      const down = diff > 0;
      this.scrollState.setNavbarHidden(down);     // runter -> Navbar hide
      this.scrollState.setFooterHidden(!down);    // hoch -> Footer hide
      this.scrollState.setDockHidden(!down);      // hoch -> Dock hide
    }

    // Klassen spiegeln (Light & Dark)
    this.applyVisibilityState(this.containerRef?.nativeElement);
    this.applyVisibilityState(this.darkContainer);

    this.last = curr;
    this.ticking = false;
  }

  private getShowAtTopPx(): number {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--nav-h').trim();
    const navH = parseFloat(v) || 64;
    return Math.max(48, Math.round(navH * 0.6));
  }

  private resetScrollBaseline(): void {
    const light = this.getLightDetailsScroller();
    const dark = this.getDarkDetailsScroller();

    const active = this.isDarkMode ? dark : light;
    if (!active) return;

    this.last = active.scrollTop || 0;
    this.curr = this.last;
  }

  private forceShowChrome(): void {
    if (this.scrollState.navbarHidden()) this.scrollState.setNavbarHidden(false);
    if (this.scrollState.footerHidden()) this.scrollState.setFooterHidden(false);
    if (this.scrollState.dockHidden()) this.scrollState.setDockHidden(false);

    this.applyVisibilityState(this.containerRef?.nativeElement);
    this.applyVisibilityState(this.darkContainer);
  }

  private applyVisibilityState(root?: HTMLElement | null) {
    if (!root) return;

    root.classList.toggle('nav-hidden', this.scrollState.navbarHidden());
    root.classList.toggle('footer-hidden', this.scrollState.footerHidden());

    // Footer (und optional Dock) im Root togglen
    const footer = root.querySelector('app-footer');
    if (footer) footer.classList.toggle('hidden', this.scrollState.footerHidden());

    // Falls du ein Dock in der Section hast:
    const dock = root.querySelector('app-dock');
    if (dock) dock.classList.toggle('hidden', this.scrollState.dockHidden());
  }

  // -------------------------
  // Wrapper logging (wie bei dir, mit cleanup)
  // -------------------------
  private initWrapperLogging(): void {
    let lastSignature = '';

    const logWrapper = () => {
      const el = this.wrapper?.nativeElement;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const values = {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        top: Math.round(rect.top),
        bottom: Math.round(rect.bottom),
        viewportH: window.innerHeight
      };

      const signature = JSON.stringify(values);
      if (signature !== lastSignature) {
        lastSignature = signature;
        console.log('[WrapperAboutPanel]', values);
      }
    };

    logWrapper();

    this.wrapperRO = new ResizeObserver(logWrapper);
    this.wrapperRO.observe(this.wrapper.nativeElement);

    this.wrapperResizeCb = logWrapper;
    window.addEventListener('resize', this.wrapperResizeCb);
  }
}
